// @owners { team: patients-team }
import { useMemo } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import { useSelectorShared } from '~shared/store';

/**
 * Returns prescription IDs for the current order in checkout.
 * For a single shipment, returns all cart prescription IDs.
 * For split shipments, returns only the prescription IDs in the current order (by order index).
 * Enables showing correct order pricing and delivery fees for split shipments.
 */
export const usePrescriptionIDsForCurrentOrder = (): number[] => {
  const { prescriptionIDs: cartPrescriptionIDs } = usePrescriptionsInCart();
  const { cartItemsByDeliveryMethod, deliveryMethods } = useCartDeliveryMethods();
  const orderIndex = useSelectorShared(getOrderIndex);

  return useMemo(() => {
    if (deliveryMethods.length <= 1) {
      return cartPrescriptionIDs;
    }
    const deliveryMethod = deliveryMethods[orderIndex];
    const deliveryMethodName = deliveryMethod?.name;
    const itemsForShipment = deliveryMethodName ? (cartItemsByDeliveryMethod[deliveryMethodName] ?? []) : [];
    return itemsForShipment.filter((item) => item.resource_type === 'Prescription').map((item) => item.resource_id);
  }, [cartPrescriptionIDs, cartItemsByDeliveryMethod, deliveryMethods, orderIndex]);
};
